import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type {
  QuestionConfigBranchLoaderResult,
  QuestionInputField,
  QuestionInputObjectValue,
  QuestionInputValue,
} from '/index';
import useGetDefaultDistanceUnit from '/business/shared/hooks/useGetDefaultDistanceUnit';
import { annualDistancesLookup } from '/business/shared/util/distances';
import formatNumber from '/shared/functions/formatNumber';
import StyledFormLabelSubtext from '/shared/styled/form/StyledFormLabelSubtext';
import type { QuestionTreeItem } from '/business/shared/functions/types';
import type { VehicleFuelTypeOption } from '/business/shared/state/selectors/application/step1/getFuelTypesSelector';
import type { VehicleSizeOption } from '/business/shared/state/selectors/application/step1/getVehicleSizesSelector';
import type { FieldValue } from '/business/shared/components/questions/helper/useQuestionSystem';
import { getTranslatedDisplayName } from '/business/shared/components/questions/helper/internals/mappedFieldValues';
import {
  ERR_TYPE_ENUM_IS_DROPDOWN,
  ERR_TYPE_FORCE,
  ERR_TYPE_NUM_LARGE,
  ERR_TYPE_NUM_SMALL,
  ERR_TYPE_REQUIRED,
  ERR_TYPE_STR_LONG,
  ERR_TYPE_STR_SHORT,
  FIELD_TYPE_DECISIONTREE,
  FIELD_TYPE_DROPDOWN,
  FIELD_TYPE_NUMBER,
  FIELD_TYPE_TXT,
  FIELD_TYPE_TXTAREA,
} from '/business/shared/components/questions/helper/fieldEnums';
type VehicleSizesFactory = (fuelType: string | null) => VehicleSizeOption[];
type UseVehicleSingleSchemaOptions = {
  fuelTypes: VehicleFuelTypeOption[];
  question: QuestionTreeItem;
  freeTypeDistance?: boolean;
  dealershipName?: string | null;
  businessName?: string | null;
  applicationId?: number | string | null;
  vehicleSizesFactory?: VehicleSizesFactory | null;
  isTotalFuelConsumptionComplete?: boolean;
  displayLesseeField?: boolean;
  prependTransportAndDistribText?: boolean;
  displayNumOfVehicles?: boolean;
  useSlugForSizeDropdown?: boolean;
};
type VehicleSchemaResult = QuestionConfigBranchLoaderResult & {
  pageSchema: QuestionInputField[];
  showSaveButton: boolean;
};
type FieldOption = NonNullable<QuestionInputField['values']>[number];
type AnnualDistanceUnit = 'km' | 'miles';
type AnnualDistanceValue = ReturnType<typeof annualDistancesLookup>[AnnualDistanceUnit][number];
const decimalInputMode: QuestionInputField['inputMode'] = 'decimal';
const busCoachFuelDisplayNames: Record<string, string> = {
  'distance-diesel': 'business:emissionFactors.vehicleFuelTypes.Diesel',
  'distance-hybrid': 'business:emissionFactors.vehicleFuelTypes.Diesel Hybrid',
  'distance-ev': 'business:emissionFactors.vehicleFuelTypes.Electric',
};
const busCoachSizeOrder = ['<7.5t', '7.5-12t', '>12t'];
const busCoachSizeNames: Record<string, string> = {
  '<7.5t': 'smallbuscoach',
  '7.5-12t': 'mediumbuscoach',
  '>12t': 'largebuscoach',
};
function getStringValue(value: QuestionInputValue | QuestionInputObjectValue | undefined): string | null {
  return typeof value === 'string' ? value : null;
}
function getNumberOrStringValue(
  value: QuestionInputValue | QuestionInputObjectValue | undefined,
): number | string | null {
  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  }
  return null;
}
function getInputValue(value: FieldValue): string | null {
  return typeof value.inputValue === 'string' ? value.inputValue : null;
}
export default function useVehicleSingleSchema({
  fuelTypes,
  question,
  freeTypeDistance = false,
  vehicleSizesFactory = null,
  displayLesseeField = false,
  displayNumOfVehicles = false,
  useSlugForSizeDropdown = true,
}: UseVehicleSingleSchemaOptions): VehicleSchemaResult {
  const { t } = useTranslation();
  const isBusCoach = question.id?.endsWith('buses-coaches') ?? false;
  const usesHighMileageDistances = isBusCoach || (question.id?.endsWith('trucks') ?? false);
  const annualDistances = annualDistancesLookup(usesHighMileageDistances);
  const [valueFuelType, setValueFuelType] = useState<string | null>(null);
  const [valueAnnualDistanceUnit, setValueAnnualDistanceUnit] = useState<string | null>(null);
  const [valueKnowsFuelEfficiency, setValueKnowsFuelEfficiency] = useState<boolean | null>(null);
  const [valueEfficiency, setValueEfficiency] = useState<number | string | null>(null);
  const [valueEfficiencyUnit, setValueEfficiencyUnit] = useState<string | null>(null);
  const [disabledFuelEfficiency, setDisabledFuelEfficiency] = useState(false);
  const { distanceUnit, defaultVolumeUnit } = useGetDefaultDistanceUnit();

  // the max efficiency for 100kml is calculate from 1mpg converted then rounded up
  let minEfficiency;
  let maxEfficiency;
  if (valueEfficiencyUnit === 'L/100km') {
    minEfficiency = 2;
    maxEfficiency = 283;
  } else if (valueEfficiencyUnit === 'kWh/100km') {
    minEfficiency = 2;
    maxEfficiency = 140;
  } else {
    minEfficiency = 0;
    maxEfficiency = 140;
  }
  const vehicleSizes = useMemo(() => {
    if (!vehicleSizesFactory) {
      return [];
    }
    const sizes = vehicleSizesFactory(valueFuelType);
    return isBusCoach
      ? [...sizes].sort((left, right) => busCoachSizeOrder.indexOf(left.title) - busCoachSizeOrder.indexOf(right.title))
      : sizes;
  }, [isBusCoach, valueFuelType, vehicleSizesFactory]);
  const disableFuelConsumptionInput = useMemo(() => {
    const average = vehicleSizes.find((vehicleSize) => {
      return vehicleSize.is_average && vehicleSize.has_volume_factor;
    });
    return !average;
  }, [vehicleSizes]);
  const vehicleSizeValues = useMemo(
    (): FieldOption[] => [
      {
        name: null,
        isPlaceholder: true,
        displayNameKey: 'business:QuestionPage.forms.vehicleTypeSelector.selectVehicleSize',
      },
      ...vehicleSizes.map(({ title, slug, name, subtitleName }) => {
        const optionTitle = getTranslatedDisplayName(title, `business:emissionFactors.vehicleSizes.${name}`, t, title);
        const translatedSubtitle = t(`business:emissionFactors.vehicleSizesSubtitles.${subtitleName}`);
        const optionSubtitle = translatedSubtitle === '___' ? '' : translatedSubtitle;
        const slugName = useSlugForSizeDropdown ? slug : title;
        return {
          name: slugName,
          displayName: `${optionTitle} ${optionSubtitle}`.trim(),
        };
      }),
    ],
    [vehicleSizes, t, useSlugForSizeDropdown],
  );
  const fuelTypeValues = useMemo(
    (): FieldOption[] => [
      {
        name: null,
        displayNameKey: 'business:QuestionPage.forms.vehicleTypeSelector.selectFuelType',
        isPlaceholder: true,
      },
      ...fuelTypes.map(({ name, displayName }) => {
        const busCoachDisplayNameKey = isBusCoach ? busCoachFuelDisplayNames[name] : undefined;
        return {
          name,
          displayNameKey:
            busCoachDisplayNameKey ?? 'business:emissionFactors.vehicleFuelTypes.'.concat(displayName),
        };
      }),
    ],
    [fuelTypes, isBusCoach],
  );
  const annualDistanceValues = useMemo(() => {
    const selectedAnnualDistanceValues: AnnualDistanceValue[] | undefined =
      valueAnnualDistanceUnit === 'km' || valueAnnualDistanceUnit === 'miles'
        ? annualDistances[valueAnnualDistanceUnit]
        : undefined;
    return [
      {
        name: null,
        displayNameKey: 'business:QuestionPage.forms.vehicleTypeSelector.selectDistance',
        isPlaceholder: true,
      },
      ...(selectedAnnualDistanceValues?.map(({ name, displayName }) => {
        return {
          name,
          displayName,
        };
      }) ?? []),
    ];
  }, [valueAnnualDistanceUnit, annualDistances]);
  const efficiencyUnitsAvailable = useMemo(() => {
    if (fuelTypes.length > 0 && valueFuelType) {
      const requestedFuel = fuelTypes.find(({ name }) => name === valueFuelType);
      if (requestedFuel) {
        return requestedFuel.efficiencyUnits.map((unitItem) => {
          return {
            name: unitItem.value,
            displayName: t(`business:emissionFactors.efficiency.${unitItem.displayKey}`, unitItem.value),
          };
        });
      }
    }
    return [];
  }, [fuelTypes, t, valueFuelType]);
  useEffect(() => {
    if (valueAnnualDistanceUnit === null) {
      setValueAnnualDistanceUnit(distanceUnit);
    }
  }, [distanceUnit, valueAnnualDistanceUnit]);
  useEffect(() => {
    if (valueKnowsFuelEfficiency === false) {
      setValueEfficiency('');
    }
  }, [valueKnowsFuelEfficiency]);

  // Default to the only fuel type if only one fuel type exists.
  useEffect(() => {
    if (fuelTypes.length === 1 && valueFuelType === '') {
      setValueFuelType(fuelTypes?.[0]?.name);
    }
  }, [valueFuelType, fuelTypes]);

  // If the average fuel type does not have a volume based EF
  // - mark MPG as disabled as it will not function (api will throw)
  useEffect(() => {
    if (freeTypeDistance) {
      setDisabledFuelEfficiency(true);
      setValueKnowsFuelEfficiency(false);
    } else if (valueFuelType) {
      if (disableFuelConsumptionInput) {
        setDisabledFuelEfficiency(true);
        setValueKnowsFuelEfficiency(false);
      } else {
        setDisabledFuelEfficiency(false);
      }
    } else if (valueFuelType === null) {
      setDisabledFuelEfficiency(false);
    }
  }, [disableFuelConsumptionInput, valueFuelType, freeTypeDistance]);
  useEffect(() => {
    if (efficiencyUnitsAvailable.length === 1) {
      if (valueEfficiencyUnit !== efficiencyUnitsAvailable[0].name) {
        setValueEfficiencyUnit(efficiencyUnitsAvailable[0].name);
      }
    }
  }, [efficiencyUnitsAvailable, valueEfficiencyUnit]);
  const supplierName = useMemo(() => {
    const label = question.apiKey?.includes('upstream') ? 'Supplier name' : 'Customer / supplier name';
    return {
      element: 'supplier_name',
      propName: 'supplierName',
      saveMe: displayLesseeField,
      renderMe: displayLesseeField,
      validateMe: displayLesseeField,
      type: FIELD_TYPE_TXT,
      label,
      labelI18nKey: '',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOIDENTIFIER',
          errStr: t('business:Errors.vehicleSingle.validIdentifier'),
        },
        {
          rule: ERR_TYPE_STR_LONG,
          errCode: 'ERRIDENTIFIERTOOSHORT',
          errStr: t('business:Errors.vehicleSingle.validIdentifier'),
          value: 3000,
        },
      ],
    };
  }, [displayLesseeField, question.apiKey, t]);
  const fuelType = useMemo(() => {
    return {
      element: 'fuel_type',
      type: FIELD_TYPE_DROPDOWN,
      propName: 'fuelType',
      values: fuelTypeValues,
      saveMeCallback: (value: FieldValue) => {
        const fuelTypeSlug = getInputValue(value);
        return {
          fuelType: fuelTypes.find(({ name }) => name === fuelTypeSlug),
        };
      },
      onChangeCallback: (value: QuestionInputValue | QuestionInputObjectValue | undefined) => {
        setValueFuelType(getStringValue(value));
      },
      labelI18nKey: 'business:QuestionPage.forms.vehicles.para3',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOFUELTYPE',
          errStr: t('business:Errors.vehicleSingle.fuelType'),
        },
        {
          rule: ERR_TYPE_ENUM_IS_DROPDOWN,
          errCode: 'ERRFUELTYPENOTFOUND',
          errStr: t('business:Errors.fuelType.notFound'),
        },
      ],
    };
  }, [fuelTypes, setValueFuelType, fuelTypeValues, t]);
  const efficencyOpts = useMemo(() => {
    return [
      {
        i18nKey: 'shared:misc.yes',
        value: valueKnowsFuelEfficiency === true,
        callback: () => {
          setValueKnowsFuelEfficiency(true);
        },
      },
      {
        i18nKey: 'shared:misc.no',
        value: valueKnowsFuelEfficiency === false,
        callback: () => {
          setValueKnowsFuelEfficiency(false);
        },
      },
    ];
  }, [valueKnowsFuelEfficiency]);
  const knowsFuelEfficiency = useMemo(() => {
    return {
      // DECISION TREE
      type: FIELD_TYPE_DECISIONTREE,
      element: 'knows-fuel-efficiency',
      renderMe: !disabledFuelEfficiency && !(valueFuelType === null || valueFuelType === ''),
      options: efficencyOpts,
      label: (
        <Trans
          i18nKey="business:QuestionPage.forms.vehicles.para4"
          components={{
            3: <br />,
            5: <StyledFormLabelSubtext $noMargin>{null}</StyledFormLabelSubtext>,
          }}
        />
      ),
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOKNOWSFUELEFFICIENCY',
          errStr: t('business:Errors.fuelType.provideEfficiencySelector'),
        },
      ],
    };
  }, [disabledFuelEfficiency, valueFuelType, efficencyOpts, t]);
  const efficiency = useMemo(() => {
    return {
      propName: 'efficiency',
      element: 'efficiency_mpg',
      inputMode: decimalInputMode,
      step: 'any',
      placeholderI18nKey: 'business:QuestionPage.forms.vehicles.fuelEfficiencyPlaceholder',
      type: FIELD_TYPE_NUMBER,
      label: (
        <Trans
          i18nKey="business:QuestionPage.forms.vehicles.para5"
          components={{
            1: <br />,
          }}
        />
      ),
      renderMe: valueKnowsFuelEfficiency === true,
      validateMe: valueKnowsFuelEfficiency === true && disabledFuelEfficiency === false,
      saveMe: valueKnowsFuelEfficiency === true && disabledFuelEfficiency === false,
      isDisabled: disabledFuelEfficiency,
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOFUELEFFICIENCY',
          errStr: t('business:Errors.fuelType.provideEfficiency'),
        },
        {
          rule: ERR_TYPE_NUM_SMALL,
          value: 0,
          errCode: 'ERRFUELEFFICIENCYTOOSMALL',
          errStr: t('business:Errors.fuelType.efficiencyTooLow'),
        },
      ],
      onChangeCallback: (value: QuestionInputValue | QuestionInputObjectValue | undefined) => {
        setValueEfficiency(getNumberOrStringValue(value));
      },
      dropdown: {
        propName: 'efficiencyUnit',
        defaultValue: defaultVolumeUnit,
        values: efficiencyUnitsAvailable,
        ifSingleNoBorder: efficiencyUnitsAvailable.length <= 1,
        onChangeCallback: (value: QuestionInputValue | QuestionInputObjectValue | undefined) => {
          setValueEfficiencyUnit(getStringValue(value));
        },
      },
    };
  }, [
    setValueEfficiencyUnit,
    efficiencyUnitsAvailable,
    setValueEfficiency,
    disabledFuelEfficiency,
    defaultVolumeUnit,
    valueKnowsFuelEfficiency,
    t,
  ]);
  const vehicleSize = useMemo(() => {
    return {
      type: FIELD_TYPE_DROPDOWN,
      element: 'vehicle_size',
      propName: 'vehicleSize',
      labelI18nKey: 'business:QuestionPage.forms.vehicles.para6',
      values: vehicleSizeValues,
      isDisabled: vehicleSizeValues?.length <= 1,
      renderMe: valueKnowsFuelEfficiency === false,
      validateMe: valueKnowsFuelEfficiency === false,
      saveMe: valueKnowsFuelEfficiency === false,
      saveMeCallback: (fieldValue: FieldValue) => {
        const vehicleSlug = getInputValue(fieldValue);
        if (!useSlugForSizeDropdown) {
          return {
            vehicleSize: vehicleSlug,
          };
        }
        const foundVehicle = vehicleSizes.find(({ slug }) => slug === vehicleSlug);
        return {
          vehicleSize: foundVehicle?.title,
          vehicleSizeName: isBusCoach
            ? (busCoachSizeNames[foundVehicle?.title ?? ''] ?? foundVehicle?.name)
            : foundVehicle?.name,
        };
      },
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOVEHICLESIZE',
          errStr: t('business:Errors.vehicleSingle.vehicleSize'),
        },
      ],
    };
  }, [isBusCoach, vehicleSizes, valueKnowsFuelEfficiency, vehicleSizeValues, useSlugForSizeDropdown, t]);
  const annualDistance = useMemo(() => {
    return {
      type: freeTypeDistance ? FIELD_TYPE_NUMBER : FIELD_TYPE_DROPDOWN,
      element: 'annual_distance',
      propName: 'annualDistance',
      renderMe: valueKnowsFuelEfficiency !== null,
      validateMe: valueKnowsFuelEfficiency !== null,
      saveMe: valueKnowsFuelEfficiency !== null,
      values: annualDistanceValues,
      label: displayLesseeField ? (
        'Total distance travelled'
      ) : (
        // @i18n: Estimated distance travelled
        <Trans i18nKey="business:QuestionPage.forms.vehicles.para7estimated" />
      ),
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOANNUALDISTANCE',
          errStr: t('business:Errors.vehicleSingle.annualDistance'),
        },
        {
          rule: ERR_TYPE_NUM_SMALL,
          errCode: 'ERRANNUALDISTANCETOOLOW',
          errStr: t('business:Errors.vehicleSingle.validDistance'),
          value: 1,
        },
        {
          rule: ERR_TYPE_NUM_LARGE,
          errCode: 'ERRANNUALDISTANCETOOHIGH',
          errStr: t('business:Errors.vehicleSingle.validDistance'),
          value: 999999,
        },
      ],
      dropdown: {
        propName: 'annualDistanceUnit',
        defaultValue: distanceUnit,
        values: [
          {
            name: 'km',
            displayName: t('business:emissionFactors.distance.km', 'km'),
          },
          {
            name: 'miles',
            displayName: t('business:emissionFactors.distance.miles', 'miles'),
          },
        ],
        onChangeCallback: (value: QuestionInputValue | QuestionInputObjectValue | undefined) => {
          setValueAnnualDistanceUnit(getStringValue(value));
        },
      },
    };
  }, [freeTypeDistance, valueKnowsFuelEfficiency, annualDistanceValues, displayLesseeField, distanceUnit, t]);
  const numOfVehicles = useMemo(() => {
    return {
      type: FIELD_TYPE_NUMBER,
      propName: 'numOfVehicles',
      element: 'num_of_vehicles',
      inputMode: decimalInputMode,
      inputMin: 1,
      inputMax: 999999,
      placeholderI18nKey: 0,
      renderMe: valueKnowsFuelEfficiency !== null && displayNumOfVehicles,
      validateMe: valueKnowsFuelEfficiency !== null && displayNumOfVehicles,
      saveMe: valueKnowsFuelEfficiency !== null && displayNumOfVehicles,
      label: (
        <Trans
          i18nKey="business:QuestionPage.forms.vehicles.para8"
          components={{
            1: <br />,
          }}
        />
      ),
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNONUMOFVEHICLES',
          errStr: t('business:Errors.vehicleSingle.validNumberOfVehicles'),
        },
        {
          rule: ERR_TYPE_NUM_SMALL,
          errCode: 'ERRNUMOFVEHICLESTOOLOW',
          errStr: t('business:Errors.vehicleSingle.validNumberOfVehicles'),
          value: 1,
        },
        {
          rule: ERR_TYPE_NUM_LARGE,
          errCode: 'ERRNUMOFVEHICLESTOOHIGH',
          errStr: t('business:Errors.vehicleSingle.validNumberOfVehicles'),
          value: 999999,
        },
      ],
      styleInputWidth: '6rem',
    };
  }, [displayNumOfVehicles, valueKnowsFuelEfficiency, t]);
  const dataSource = useMemo(() => {
    return {
      type: FIELD_TYPE_TXTAREA,
      propName: 'howCollected',
      element: 'data-source',
      label: 'How was this data collected:',
      description: <></>,
      renderMe: valueKnowsFuelEfficiency !== null,
      validateMe: valueKnowsFuelEfficiency !== null,
      saveMe: valueKnowsFuelEfficiency !== null,
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRDATASOURCE',
          errStr: t('business:Errors.dataSource.provide'),
        },
        {
          rule: ERR_TYPE_STR_SHORT,
          errCode: 'ERRDATASOURCETOOSHORT',
          errStr: t('business:Errors.dataSource.tooShort'),
          value: 5,
        },
        {
          rule: ERR_TYPE_STR_LONG,
          errCode: 'ERRDATASOURCETOOLONG',
          errStr: t('business:Errors.dataSource.tooLong'),
          value: 3000,
        },
      ],
    };
  }, [displayNumOfVehicles, valueKnowsFuelEfficiency, t]);

  // Inject a validator FORCE ERROR if the following checks match up
  let efficiencyErrorFormatted = null;
  let errMsg = '';
  let errCode = '';
  if (Number(valueEfficiency) > maxEfficiency) {
    efficiencyErrorFormatted = formatNumber(maxEfficiency);
    errMsg = 'efficiencyTooHigh';
    errCode = 'ERRFUELEFFICIENCYTOOHIGH';
  } else if (Number(valueEfficiency) < minEfficiency && minEfficiency !== 0) {
    efficiencyErrorFormatted = formatNumber(minEfficiency);
    errMsg = 'efficiencyTooLow';
    errCode = 'ERRFUELEFFICIENCYTOOLOW';
  }
  if (efficiencyErrorFormatted && errMsg !== '' && errCode !== '') {
    if (!efficiency.validation.find((rule) => rule.errCode === errCode)) {
      efficiency.validation.push({
        rule: ERR_TYPE_FORCE,
        errCode: errCode,
        errStr: t(`business:Errors.fuelType.${errMsg}`, {
          efficiencyErrorFormatted,
          efficiencyUnit: valueEfficiencyUnit,
        }),
      });
    }
  }
  if (!errMsg && !errCode) {
    efficiency.validation = efficiency.validation.filter((rule) => rule.rule !== ERR_TYPE_FORCE);
  }
  const pageSchema = useMemo<QuestionInputField[]>(
    () => [
      supplierName,
      fuelType,
      knowsFuelEfficiency,
      vehicleSize,
      efficiency,
      annualDistance,
      numOfVehicles,
      dataSource,
    ],
    [supplierName, fuelType, knowsFuelEfficiency, vehicleSize, efficiency, annualDistance, numOfVehicles, dataSource],
  );
  return {
    pageSchema,
    showSaveButton: valueKnowsFuelEfficiency !== null,
  };
}
